import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import WeekPlanner from "@/components/WeekPlanner";
import { FadeUp } from "@/components/Reveal";
import { Spark, Starburst } from "@/components/Doodles";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Trash2, CalendarClock } from "lucide-react";

const TIME_OPTIONS = Array.from({ length: 17 }, (_, i) => {
    const h24 = 7 + i;
    const label = `${h24 % 12 === 0 ? 12 : h24 % 12}:00 ${h24 < 12 ? "AM" : "PM"}`;
    return { value: `${String(h24).padStart(2, "0")}:00`, label };
});

const todayIso = () => new Date().toISOString().slice(0, 10);
const maxIso = () => new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);

const DETAIL_CHIPS = [
    "Open to members + non-members",
    "$25/hr standard rate",
    "Member discounts + included hours",
    "Exclusive — the whole room is yours",
    "Meetings, calls, recording + crafts welcome",
    "Members drop in free when it's not reserved",
    "Reasonable noise is fine during your booking",
];

const inputCls = "mt-1 w-full border-2 border-ink bg-cream px-4 py-3 text-ink placeholder:text-ink/40";
const labelCls = "block text-sm font-semibold text-ink";

const LoginForm = () => {
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(email, password);
            toast.success("Welcome back to the clubhouse.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
        setBusy(false);
    };

    return (
        <form onSubmit={submit} data-testid="workroom-login-form" className="border-2 border-ink bg-cream p-7 hard-shadow-lg -rotate-[0.5deg]">
            <h2 className="font-display text-2xl tracking-wide text-ink">MEMBERS, SIGN IN</h2>
            <p className="mt-1 text-sm text-ink/70">Your member rate and included hours apply themselves.</p>
            <label className="mt-4 block text-sm font-semibold text-ink" htmlFor="wr-login-email">Email</label>
            <input id="wr-login-email" data-testid="wr-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            <label className="mt-4 block text-sm font-semibold text-ink" htmlFor="wr-login-password">Password</label>
            <input id="wr-login-password" data-testid="wr-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
            <button type="submit" disabled={busy} data-testid="wr-login-submit" className="btn-stamp mt-5 w-full !bg-forest disabled:opacity-60">
                {busy ? "Checking the list…" : "Sign In"}
            </button>
            <p className="mt-3 text-xs text-ink/60">
                <Link to="/reset-password" className="font-semibold text-forest underline decoration-mustard decoration-2 underline-offset-2">Forgot password?</Link>
                {" · "}
                <Link to="/activate" className="font-semibold text-forest underline decoration-mustard decoration-2 underline-offset-2">Paying dues but never set a password?</Link>
            </p>
        </form>
    );
};

const BookingCard = ({ user, rates, onBooked }) => {
    const [form, setForm] = useState({ date: todayIso(), start: "10:00", hours: 1, purpose: "", name: "", email: "" });
    const [quote, setQuote] = useState(null);
    const [busy, setBusy] = useState(false);
    const isMember = user && (user.role === "admin" || !!user.membership_tier);

    useEffect(() => {
        const id = setTimeout(() => {
            api.post("/studio/quote", { date: form.date, start: form.start, hours: Number(form.hours) })
                .then((r) => setQuote(r.data))
                .catch(() => setQuote(null));
        }, 250);
        return () => clearTimeout(id);
    }, [form.date, form.start, form.hours]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const endpoint = isMember ? "/studio/bookings" : "/studio/book-guest";
            const payload = isMember
                ? { ...form, hours: Number(form.hours), origin_url: window.location.origin }
                : { ...form, hours: Number(form.hours), origin_url: window.location.origin };
            const { data } = await api.post(endpoint, payload);
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
                return;
            }
            toast.success("Booked — covered by your included hours. Go make the thing.");
            setForm({ ...form, purpose: "" });
            onBooked();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
        setBusy(false);
    };

    return (
        <form onSubmit={submit} data-testid="workroom-booking-form" className="border-2 border-ink bg-paper p-7 hard-shadow-lg -rotate-[0.5deg] sm:p-8">
            {user ? (
                <p className="-rotate-1 font-hand text-2xl text-orange">
                    signed in as {user.name || user.email} ·{" "}
                    <Link to="/account" data-testid="workroom-account-link" className="underline decoration-mustard decoration-2 underline-offset-4">my clubhouse card</Link>
                </p>
            ) : null}
            <h2 className="mt-1 font-display text-3xl tracking-wide text-ink">{isMember ? "RESERVE YOUR SPOT" : "RESERVE AS A NEIGHBOR"}</h2>

            <label className="mt-5 block text-sm font-semibold text-ink" htmlFor="wr-date">Date</label>
            <input id="wr-date" data-testid="wr-date-input" type="date" required min={todayIso()} max={maxIso()}
                value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
            <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                    <label className={labelCls} htmlFor="wr-start">Start</label>
                    <select id="wr-start" data-testid="wr-start-select" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className={inputCls}>
                        {TIME_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className={labelCls} htmlFor="wr-hours">Hours</label>
                    <select id="wr-hours" data-testid="wr-hours-select" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className={inputCls}>
                        {[1, 2, 3].map((h) => <option key={h} value={h}>{h} {h === 1 ? "hour" : "hours"}</option>)}
                    </select>
                </div>
            </div>

            {quote && (
                <div data-testid="wr-quote" className="mt-5 border-2 border-dashed border-ink/50 bg-cream p-4">
                    <div className="flex items-center justify-between text-sm font-semibold text-ink/80">
                        <span>{quote.is_member ? `${quote.tier_name} rate` : "Public rate"}</span>
                        <span data-testid="wr-quote-rate">${quote.rate.toFixed(2)}/hr</span>
                    </div>
                    {quote.included_hours_used > 0 && (
                        <div className="mt-1 flex items-center justify-between text-sm font-semibold text-forest">
                            <span>Included hours applied</span>
                            <span data-testid="wr-quote-included">− {quote.included_hours_used} hr{quote.included_hours_used > 1 ? "s" : ""} (on the house)</span>
                        </div>
                    )}
                    <div className="mt-2 flex items-center justify-between border-t-2 border-ink pt-2 font-display text-xl tracking-wide text-ink">
                        <span>TOTAL</span>
                        <span data-testid="wr-quote-total">{quote.free ? "$0 — covered!" : `$${quote.total.toFixed(2)}`}</span>
                    </div>
                    <p className={`mt-2 text-xs font-semibold ${quote.available ? "text-forest" : "text-brick"}`} data-testid="wr-quote-availability">
                        {quote.available
                            ? "The room is open for that slot — it'd be all yours."
                            : quote.reason || "Someone's got the whole room then — try another time."}
                    </p>
                </div>
            )}

            {!isMember && (
                <>
                    <div className="mt-5 grid gap-4 sm:grid-cols-2">
                        <div>
                            <label className={labelCls} htmlFor="wr-name">Name</label>
                            <input id="wr-name" data-testid="wr-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls} htmlFor="wr-email">Email</label>
                            <input id="wr-email" data-testid="wr-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                        </div>
                    </div>
                    <div data-testid="wr-member-nudge" className="mt-5 border-2 border-ink bg-mustard p-4 hard-shadow">
                        <p className="font-display text-lg tracking-wide text-ink">HERE OFTEN? MEMBERSHIP MIGHT SAVE YOU MONEY.</p>
                        <p className="mt-1 text-sm text-ink/75">Every membership includes free shared drop-in access, plus discounted private reservation rates and other perks. Take a peek before you book.</p>
                        <div className="mt-3 flex flex-wrap gap-3">
                            <Link to="/membership" data-testid="wr-nudge-join" className="border-2 border-ink bg-cream px-4 py-2 font-display text-sm tracking-wider text-ink hard-shadow transition-transform duration-150 hover:-translate-y-0.5">
                                Explore Memberships
                            </Link>
                            <span className="self-center font-display text-sm tracking-wider text-ink/60">Continue as Guest — ${rates?.public_rate?.toFixed(2) || "25.00"}/hr below ↓</span>
                        </div>
                    </div>
                </>
            )}

            <label className="mt-5 block text-sm font-semibold text-ink" htmlFor="wr-purpose">What are you making? (optional)</label>
            <input id="wr-purpose" data-testid="wr-purpose-input" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                placeholder="Novel chapter, quilt square, quiet revenge…" className={inputCls} />

            <button data-testid="wr-booking-submit" type="submit" disabled={busy || (quote && !quote.available)} className="btn-stamp mt-6 w-full !bg-orange disabled:opacity-60">
                {busy ? "Pinning it down…" : quote?.free ? "Reserve — covered by included hours" : isMember ? "Reserve at Member Rate" : `Continue as Guest${quote ? ` — $${quote.total.toFixed(2)}` : ""}`}
            </button>
            <p className="mt-3 text-xs text-ink/60">
                7 AM – midnight · up to 3 hours · 14 days ahead · cancel or reschedule 12+ hours ahead. A reservation is exclusive — the whole room is yours.
            </p>
            <p data-testid="wr-dropin-reminder" className="mt-4 border-2 border-dashed border-ink/40 bg-cream px-4 py-3 text-sm text-ink/75">
                <strong className="font-display tracking-wide text-ink">JUST NEED A PLACE TO WORK OR MAKE SOMETHING?</strong>{" "}
                Members can drop in for free when the room isn't reserved. Booking is only necessary when you want the whole room to yourself.
            </p>
        </form>
    );
};

const MemberReservations = ({ user, refreshKey, onChanged }) => {
    const [mine, setMine] = useState(null);
    const [moving, setMoving] = useState(null);
    const [moveForm, setMoveForm] = useState({ date: todayIso(), start: "10:00" });

    const load = () => api.get("/studio/bookings/mine").then((r) => setMine(r.data));
    useEffect(() => {
        if (user) load();
    }, [user, refreshKey]);

    const cancel = async (id) => {
        try {
            await api.delete(`/studio/bookings/${id}`);
            toast.success("Reservation released back into the wild.");
            onChanged();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const reschedule = async (id) => {
        try {
            await api.post(`/studio/bookings/${id}/reschedule`, moveForm);
            toast.success("Moved. Same room, new day.");
            setMoving(null);
            onChanged();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    if (!user || (!user.membership_tier && user.role !== "admin")) return null;
    const upcoming = (mine || []).filter((b) => b.status !== "cancelled");

    return (
        <div className="border-2 border-ink bg-forest p-7 hard-shadow-lg rotate-[0.5deg] sm:p-8">
            <h2 className="font-display text-3xl tracking-wide text-cream">YOUR RESERVATIONS</h2>
            <ul className="mt-5 space-y-3">
                {upcoming.map((b) => (
                    <li key={b.id} data-testid={`my-booking-${b.id}`} className="border-2 border-ink bg-paper px-4 py-3 text-ink hard-shadow">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="font-display text-lg tracking-wide">
                                    {new Date(`${b.date}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} · {b.start} · {b.hours} hr{b.hours > 1 ? "s" : ""}
                                </p>
                                <p className="text-sm text-ink/60">
                                    {b.purpose || ""}{b.status === "pending_payment" ? " · awaiting payment" : ""}{b.amount ? ` · $${b.amount.toFixed(2)}` : " · covered"}
                                </p>
                            </div>
                            <div className="flex shrink-0 gap-2">
                                <button data-testid={`booking-move-${b.id}`} onClick={() => { setMoving(moving === b.id ? null : b.id); setMoveForm({ date: b.date, start: b.start }); }} aria-label="Reschedule reservation"
                                    className="border-2 border-ink bg-mustard p-2 text-ink hard-shadow">
                                    <CalendarClock className="h-4 w-4" />
                                </button>
                                <button data-testid={`booking-cancel-${b.id}`} onClick={() => cancel(b.id)} aria-label="Cancel reservation"
                                    className="border-2 border-ink bg-brick p-2 text-cream hard-shadow">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                        {moving === b.id && (
                            <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-dashed border-ink/30 pt-3">
                                <input type="date" value={moveForm.date} min={todayIso()} max={maxIso()} onChange={(e) => setMoveForm({ ...moveForm, date: e.target.value })}
                                    className="border-2 border-ink bg-cream px-2 py-1.5 text-sm" data-testid={`move-date-${b.id}`} />
                                <select value={moveForm.start} onChange={(e) => setMoveForm({ ...moveForm, start: e.target.value })}
                                    className="border-2 border-ink bg-cream px-2 py-1.5 text-sm" data-testid={`move-start-${b.id}`}>
                                    {TIME_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                                <button onClick={() => reschedule(b.id)} data-testid={`move-save-${b.id}`} className="border-2 border-ink bg-forest px-3 py-1.5 font-display text-sm tracking-wider text-cream hard-shadow">
                                    Move It
                                </button>
                            </div>
                        )}
                    </li>
                ))}
                {mine && upcoming.length === 0 && (
                    <p data-testid="my-bookings-empty" className="font-hand text-2xl text-cream/75">Nothing booked yet. The room awaits your genius.</p>
                )}
            </ul>
            <p className="mt-4 text-sm text-cream/70">
                Receipts + full history live on <Link to="/account" className="font-semibold text-mustard underline underline-offset-4">your clubhouse card</Link>.
            </p>
        </div>
    );
};

const RegularCTA = ({ rates }) => (
    <section data-testid="workroom-regular-cta" className="border-y-2 border-ink bg-mustard px-5 py-16 sm:px-10 sm:py-20">
        <div className="mx-auto grid max-w-6xl items-start gap-10 lg:grid-cols-2">
            <FadeUp>
                <p className="-rotate-2 font-hand text-2xl text-brick sm:text-3xl">the math is friendly here</p>
                <h2 className="mt-1 font-display text-4xl tracking-wide text-ink sm:text-5xl">MAKE YOURSELF A REGULAR.</h2>
                <p className="mt-4 max-w-lg leading-relaxed text-ink/80">
                    Every paid membership includes free shared access to the work room — drop in whenever it isn't reserved, no booking
                    needed. And when you do want the whole room to yourself, members unlock lower private-reservation rates, and some
                    memberships even include exclusive hours each month. Come for the workspace, stay for the community.
                </p>
                <Link to="/membership" data-testid="regular-cta-button" className="btn-stamp mt-6 inline-block !bg-brick !text-xl">
                    Explore Memberships →
                </Link>
            </FadeUp>
            <FadeUp delay={0.12}>
                <div className="border-2 border-ink bg-cream p-6 hard-shadow-lg rotate-1">
                    <h3 className="font-display text-xl tracking-wider text-ink">THE HONEST RATE CARD — WHOLE-ROOM RESERVATIONS</h3>
                    <table data-testid="workroom-rate-table" className="mt-4 w-full text-left text-sm">
                        <thead>
                            <tr className="border-b-2 border-ink font-display text-xs tracking-wider text-ink/60">
                                <th className="pb-2">Tier</th>
                                <th className="pb-2">Private reservation rate</th>
                                <th className="pb-2">Included hours</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-b border-ink/20">
                                <td className="py-2 font-semibold">No membership</td>
                                <td className="py-2">${rates?.public_rate?.toFixed(2) ?? "25.00"}/hr</td>
                                <td className="py-2 text-ink/50">—</td>
                            </tr>
                            {(rates?.tiers || []).map((t) => (
                                <tr key={t.tier} className="border-b border-ink/20" data-testid={`rate-row-${t.tier}`}>
                                    <td className="py-2 font-semibold">{t.name} <span className="text-ink/50">(${t.dues}/mo)</span></td>
                                    <td className="py-2">{t.rate !== null ? `$${t.rate.toFixed(2)}/hr` : `$${rates.public_rate.toFixed(2)}/hr`}</td>
                                    <td className="py-2">{t.included_hours ? `${t.included_hours} hrs/mo` : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <p className="mt-3 text-xs text-ink/60">Every paid membership already includes free shared drop-in access — the rates above only apply when you reserve the whole room to yourself. Membership dues are separate.</p>
                </div>
            </FadeUp>
        </div>
    </section>
);

const WorkRoom = () => {
    const { user } = useAuth();
    const [rates, setRates] = useState(null);
    const [calKey, setCalKey] = useState(0);

    useEffect(() => {
        api.get("/studio/rates").then((r) => setRates(r.data)).catch(() => {});
    }, []);

    const refresh = () => setCalKey((k) => k + 1);

    return (
        <>
            <Seo title="Reserve the Work Room" description="The Work Room at Panther City Clubhouse in Richland Hills, TX — a flexible shared workspace, free for members to drop in. Reserve the entire room exclusively for meetings, calls, recordings, and projects: $25/hr public, member discounts available." />
            <header className="mx-auto max-w-6xl px-5 pb-10 pt-32 sm:px-10 sm:pt-40">
                <FadeUp>
                    <p className="inline-block -rotate-2 font-hand text-2xl text-orange sm:text-3xl">a little space to do your thing</p>
                    <h1 className="mt-2 font-display text-5xl leading-[0.95] tracking-wide text-ink sm:text-6xl lg:text-7xl">THE WORK ROOM</h1>
                    <p className="mt-4 max-w-2xl leading-relaxed text-ink/80">
                        Need somewhere to set up your laptop, spread out a craft project, or get a little work done? The Work Room is a
                        flexible shared workspace for Clubhouse members. Members can drop in and use the room for free whenever it's
                        available. No booking needed — grab an open spot, get comfortable, and do your thing.
                    </p>
                    <p className="mt-4 max-w-2xl leading-relaxed text-ink/80">
                        <strong className="font-display tracking-wide text-ink">NEED THE WHOLE PLACE TO YOURSELF?</strong> Reserve the
                        entire Work Room for meetings, Zoom calls, recordings, collaborative projects, or anything else that benefits
                        from a little privacy and room to spread out. When you reserve the room, it's yours for the duration of your booking.
                    </p>
                </FadeUp>
                <FadeUp delay={0.1} className="mt-6 flex flex-wrap gap-2.5">
                    {DETAIL_CHIPS.map((c, i) => (
                        <span key={c} className={`inline-block border-2 border-ink bg-cream px-3 py-1 font-display text-sm tracking-wider text-ink hard-shadow ${i % 2 ? "rotate-1" : "-rotate-1"}`}>
                            {c}
                        </span>
                    ))}
                </FadeUp>
            </header>

            <section data-testid="workroom-two-ways" className="mx-auto max-w-6xl px-5 pb-14 sm:px-10">
                <FadeUp>
                    <h2 className="font-display text-3xl tracking-wide text-ink sm:text-4xl">TWO WAYS TO USE THE ROOM</h2>
                </FadeUp>
                <div className="mt-8 grid gap-8 md:grid-cols-2">
                    <FadeUp>
                        <div className="h-full border-2 border-ink bg-forest p-7 text-cream hard-shadow-lg -rotate-[0.5deg] sm:p-8">
                            <p className="-rotate-2 font-hand text-2xl text-mustard">included with every membership</p>
                            <h3 className="mt-1 font-display text-3xl tracking-wide">DROP IN &amp; SHARE</h3>
                            <ul className="mt-4 space-y-2 text-sm leading-relaxed text-cream/85">
                                <li>· No reservation required.</li>
                                <li>· Shared workspace with other members.</li>
                                <li>· First-come, first-served.</li>
                                <li>· Available when the room isn't privately reserved.</li>
                                <li>· Laptops, reading, studying, crafts, creative projects.</li>
                            </ul>
                            <Link to="/membership" data-testid="two-ways-membership-cta" className="btn-stamp mt-6 inline-block !bg-mustard !text-ink">
                                Explore Memberships
                            </Link>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.12}>
                        <div className="h-full border-2 border-ink bg-orange p-7 text-cream hard-shadow-lg rotate-[0.5deg] sm:p-8">
                            <p className="rotate-2 font-hand text-2xl text-mustard">exclusive use by the hour</p>
                            <h3 className="mt-1 font-display text-3xl tracking-wide">BOOK THE WHOLE ROOM</h3>
                            <ul className="mt-4 space-y-2 text-sm leading-relaxed text-cream/90">
                                <li>· Private space for your booking — the whole room is yours.</li>
                                <li>· Meetings, calls, recordings, crafts, and more.</li>
                                <li>· Member discounts and included hours where applicable.</li>
                                <li>· Available to nonmembers at the standard hourly rate.</li>
                                <li>· Reasonable noise is fine — it doesn't have to stay quiet.</li>
                            </ul>
                            <a href="#reserve" data-testid="two-ways-reserve-cta" className="btn-stamp mt-6 inline-block !bg-cream !text-ink">
                                Reserve the Work Room
                            </a>
                        </div>
                    </FadeUp>
                </div>
            </section>

            <section id="reserve" className="mx-auto max-w-6xl scroll-mt-28 px-5 pb-16 sm:px-10">
                <FadeUp className="mb-10">
                    <h2 className="font-display text-2xl tracking-wider text-ink">THE WEEK, BLOCK BY BLOCK</h2>
                    <p className="mt-1 text-sm text-ink/60">Browse freely — no name, no email, no commitment until you're ready. A reserved block means the whole room is spoken for; drop-in shared use pauses until the booking ends.</p>
                    <div className="mt-4">
                        <WeekPlanner refreshKey={calKey} />
                    </div>
                </FadeUp>

                <div className="grid items-start gap-10 lg:grid-cols-[1fr_1.1fr]">
                    <FadeUp>
                        <BookingCard user={user} rates={rates} onBooked={refresh} />
                        {!user && (
                            <div className="mt-8">
                                <LoginForm />
                            </div>
                        )}
                    </FadeUp>
                    <FadeUp delay={0.12}>
                        {user ? (
                            <MemberReservations user={user} refreshKey={calKey} onChanged={refresh} />
                        ) : (
                            <div className="border-2 border-dashed border-ink/50 bg-cream p-8 rotate-[0.5deg]">
                                <Spark className="h-8 w-8 text-orange" />
                                <h2 className="mt-2 font-display text-2xl tracking-wide text-ink">HOW IT WORKS</h2>
                                <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-ink/75">
                                    <li>Pick a date, start time, and duration above — the price shows itself before you commit.</li>
                                    <li>Pay securely online (members at their rate, neighbors at $25/hr).</li>
                                    <li>Confirmation lands in your email with a manage link — view, reschedule, or cancel 12+ hours out, no account needed.</li>
                                    <li>Show up, settle in, make the thing.</li>
                                </ol>
                                <p className="mt-5 -rotate-1 font-hand text-2xl text-brick">members: sign in first and your rate applies itself.</p>
                            </div>
                        )}
                    </FadeUp>
                </div>
            </section>

            <RegularCTA rates={rates} />

            <section className="px-5 py-14 text-center sm:px-10">
                <FadeUp>
                    <Starburst className="mx-auto h-24 w-24 text-forest" lines={["YOURS", "TO BOOK"]} />
                    <p className="mx-auto mt-5 max-w-xl font-hand text-2xl text-ink/70">good light, room to spread out, zero meetings about meetings.</p>
                </FadeUp>
            </section>
            <Marquee />
        </>
    );
};

export default WorkRoom;
